import { useEffect, useRef, useState } from 'react'
import { useImagePixels } from './useImagePixels'
import { Caption, COLORS, Badge } from './ui'

const W = 64, H = 54

const MODES = [
  { key: 'original', label: 'Original', desc: 'source image', color: '#6366f1' },
  { key: 1, label: 'Semantic', desc: 'class per pixel', color: '#a855f7' },
  { key: 2, label: 'Instance', desc: 'individual masks', color: '#ec4899' },
  { key: 3, label: 'Panoptic', desc: 'semantic + instance', color: '#f97316' },
]

// Same warm-mid-tone heuristic the 3D version used — illustrative, not a real model.
function isCatPixel(r, g, b) {
  const maxC = Math.max(r, g, b), minC = Math.min(r, g, b)
  const sat = maxC > 0 ? (maxC - minC) / maxC : 0
  const bright = (r + g + b) / 3
  return bright > 40 && bright < 220 && (sat > 0.08 || r - b > 20)
}

const PAL = {
  1: { cat: [99, 102, 241], bg: [22, 22, 44] },
  2: { a: [168, 85, 247], b: [99, 102, 241], bg: [22, 22, 44] },
  3: { a: [251, 146, 60], b: [168, 85, 247], c: [99, 102, 241], bg: [22, 22, 44] },
}

function MaskCanvas({ pixels, mode, color, imgSize }) {
  const ref = useRef(null)
  useEffect(() => {
    if (!pixels || !ref.current) return
    const ctx = ref.current.getContext('2d')
    const img = ctx.createImageData(W, H)
    for (let i = 0; i < W * H; i++) {
      const [r, g, b] = pixels[i]
      const cat = isCatPixel(r, g, b)
      const row = Math.floor(i / W), col = i % W
      const top = row < H * 0.5, left = col < W * 0.5
      let c
      if (mode === 1) c = cat ? PAL[1].cat : PAL[1].bg
      else if (mode === 2) c = cat ? (top ? PAL[2].a : PAL[2].b) : PAL[2].bg
      else c = cat ? (top ? (left ? PAL[3].a : PAL[3].b) : PAL[3].c) : PAL[3].bg
      const idx = i * 4
      img.data[idx] = c[0]; img.data[idx + 1] = c[1]; img.data[idx + 2] = c[2]; img.data[idx + 3] = 255
    }
    ctx.putImageData(img, 0, 0)
  }, [pixels, mode])
  return <canvas ref={ref} width={W} height={H} style={{ ...imgSize, borderRadius: 10, border: `1px solid ${color}55` }} />
}

export function Segmentation2D() {
  const pixels = useImagePixels('/cv/ref-cat.png', W, H)
  const [mobile, setMobile] = useState(() => window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  const imgSize = mobile ? { width: 220, height: 185 } : { width: 150, height: 126 }

  return (
    <div style={{
      position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto',
      alignItems: 'center', justifyContent: 'center', gap: 16, padding: mobile ? '80px 20px 90px' : '90px 24px 110px',
      fontFamily: "'General Sans', system-ui, sans-serif", textAlign: 'center', color: COLORS.text,
    }}>
      <Badge color={COLORS.pink}>Segmentation — a label for every pixel</Badge>

      <div style={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', gap: mobile ? 14 : 16, flexWrap: 'wrap', justifyContent: 'center', width: mobile ? '100%' : undefined }}>
        {MODES.map(m => (
          <div key={String(m.key)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            {m.key === 'original'
              ? <img src="/cv/ref-cat.png" alt="cat" style={{ ...imgSize, objectFit: 'cover', borderRadius: 10, border: `1px solid ${m.color}55` }} />
              : <MaskCanvas pixels={pixels} mode={m.key} color={m.color} imgSize={imgSize} />}
            <div style={{ fontSize: 14.5, fontWeight: 700, color: m.color }}>{m.label}</div>
            <div style={{ fontSize: 12.5, color: COLORS.textDim }}>{m.desc}</div>
          </div>
        ))}
      </div>

      <Caption>
        From a class for every pixel (semantic) to separating each object (instance) and merging both (panoptic).
        In 2026, <strong>SAM&nbsp;2</strong> made this <em>promptable</em> and zero-shot — click or box anything,
        in images or video, and it returns a precise mask without task-specific training.
      </Caption>
    </div>
  )
}
